import { Injectable } from '@angular/core';
import { Observable, of, forkJoin } from 'rxjs';
import { switchMap, map, catchError, mergeMap, tap } from 'rxjs/operators';
import { PbiTimeByColumnResult } from '../models/team-metrics';
import { AdoService } from './ado.service';

/**
 * Service responsible for calculating the time Product Backlog Items (PBIs)
 * spend in different board columns. It fetches data from Azure DevOps (ADO),
 * processes work item revisions, and aggregates the time spent in each column.
 */
@Injectable({
  providedIn: 'root'
})
export class PbiTimeByColumnCalculatorService {
  private readonly boardColumnsCache = new Map<string, any[]>();
  private readonly workItemRevisionsCache = new Map<string, any[][]>();
  private readonly excludedStates = ['New', 'Done', 'Approved'];

  /**
   * @param adoService Service for interacting with the Azure DevOps API.
   */
  constructor(
    private readonly adoService: AdoService,
  ) {}

  /**
   * Calculates the time spent by PBIs in various board columns for a given team and time period.
   * @param teamName The name of the team.
   * @param startDate The start date of the reporting period in ISO format.
   * @param endDate The end date of the reporting period in ISO format.
   * @param org The ADO organization name.
   * @param projectName The ADO project name.
   * @returns An Observable that emits a `PbiTimeByColumnResult` object.
   */
  public calculate(
    teamName: string,
    startDate: string,
    endDate: string,
    org: string,
    projectName: string
  ): Observable<PbiTimeByColumnResult> {
    return this.getBoardColumns(org, projectName, teamName).pipe(
      switchMap(boardColumns => {
        const filteredColumnsByState = this.filterColumnsByState(boardColumns);
        
        return this.getWorkItemRevisions(org, projectName, teamName, startDate, endDate).pipe(
          map(revisionsArray => {
            if (!revisionsArray || revisionsArray.length === 0) {
              return {
                value: 0,
                displayValue: 'N/A',
                subtitle: 'No completed PBIs found in period',
                columns: [],
                totalPbis: 0,
                allColumnsWithData: filteredColumnsByState
              };
            }
            return this.calculateColumnTimes(revisionsArray, filteredColumnsByState, filteredColumnsByState, teamName);
          }),
          map(result => ({ ...result, columns: this.sortColumns(result.columns, filteredColumnsByState) }))
        );
      }),
      catchError(error => {
        console.error('Error calculating PBI Time by Column:', error);
        return of({
          value: 0,
          displayValue: 'N/A',
          subtitle: 'Error retrieving data',
          columns: [],
          totalPbis: 0
        });
      })
    );
  }

  /**
   * Filters board columns to exclude those mapping to terminal or initial states like 'New' or 'Done'.
   * @param boardColumns The array of board column objects from ADO.
   * @returns An array of column names that should be included in time tracking.
   */
  private filterColumnsByState(boardColumns: any[]): string[] {
    return boardColumns
      .filter(c => c.stateMappings && Object.keys(c.stateMappings).length > 0)
      .filter(c => {
        const states = Object.values(c.stateMappings);
        return !states.some(state => this.excludedStates.includes(state as string));
      })
      .map(c => c.name);
  }

  /**
   * Fetches all revisions for work items that were completed within the specified date range.
   * Uses a cache to avoid redundant API calls.
   * @param org The ADO organization name.
   * @param projectName The ADO project name.
   * @param teamName The name of the team.
   * @param startDate The start date of the reporting period.
   * @param endDate The end date of the reporting period.
   * @returns An Observable that emits an array of revision arrays, one for each work item.
   */
  private getWorkItemRevisions(org: string, projectName: string, teamName: string, startDate: string, endDate: string): Observable<any[][]> {
    const cacheKey = `${projectName}|${teamName}|${startDate}|${endDate}`;
    const cachedData = this.workItemRevisionsCache.get(cacheKey);

    if (cachedData) {
      return of(cachedData);
    }
    
    return this.adoService.ExecuteWiqlQuery(org, projectName, this.buildWiqlQuery(projectName, teamName, startDate, endDate)).pipe(
      switchMap(response => {
        const workItemIds = response.workItems.map((item: any) => item.id);
        if (workItemIds.length === 0) {
          return of([]);
        }
        return of(workItemIds).pipe(
          mergeMap((ids: number[]) => 
            forkJoin(
              ids.map((id: number) => 
                this.adoService.GetWorkItemRevisions(org, projectName, id)
              )
            )
          )
        );
      }),
      tap(revisions => {
        this.workItemRevisionsCache.set(cacheKey, revisions);
      })
    );
  }

  /**
   * Constructs a WIQL query to find PBIs and Bugs completed within a specific date range for a team.
   * @param projectName The ADO project name.
   * @param teamName The name of the team.
   * @param startDate The start date for the query.
   * @param endDate The end date for the query.
   * @returns A WIQL query string.
   */
  private buildWiqlQuery(projectName: string, teamName: string, startDate: string, endDate: string): string {
    return `
      SELECT [System.Id]
      FROM workitems
      WHERE
        [System.TeamProject] = '${projectName}'
        AND [System.WorkItemType] IN ('Bug', 'Product Backlog Item')
        AND [System.State] = 'Done'
        AND [System.AreaPath] = @TeamAreas('[${projectName}]\\${teamName}')
        AND [Microsoft.VSTS.Common.StateChangeDate] >= '${startDate}'
        AND [Microsoft.VSTS.Common.StateChangeDate] <= '${endDate}'
    `;
  }

  /**
   * Processes work item revisions to calculate the total time spent in each board column.
   * @param revisionsArray An array of revision arrays for multiple work items.
   * @param columnOrder The ordered list of columns to include in the calculation.
   * @param allColumns All available columns, used for building the final result.
   * @param teamName The name of the team, passed for logging purposes.
   * @returns A `PbiTimeByColumnResult` object containing the aggregated data.
   */
  private calculateColumnTimes(revisionsArray: any[][], columnOrder: string[], allColumns: string[], teamName: string): PbiTimeByColumnResult {
    const { aggregatedColumnTimes, pbiIds } = this.aggregateTimeFromRevisions(revisionsArray, columnOrder, teamName);
    const totalPbis = pbiIds.size;
    const { averageColumnTimes, averageTotalTime } = this.calculateAverageTimes(aggregatedColumnTimes, totalPbis);

    return this.buildResult(averageColumnTimes, averageTotalTime, totalPbis, allColumns);
  }

  /**
   * Aggregates the total time spent in each column across all provided work item revisions.
   * @param revisionsArray An array of revision arrays for multiple work items.
   * @param columnOrder The ordered list of columns to include in the calculation.
   * @param teamName The name of the team, passed for logging purposes.
   * @returns An object containing the aggregated times and the set of unique PBI IDs.
   */
  private aggregateTimeFromRevisions(revisionsArray: any[][], columnOrder: string[], teamName: string): { aggregatedColumnTimes: { [key: string]: number }, pbiIds: Set<number> } {
    const aggregatedColumnTimes: { [column: string]: number } = {};
    const pbiIds = new Set<number>();

    revisionsArray.forEach((revisions: any[]) => {
      if (revisions.length === 0) {
        return;
      }
      const pbiId = revisions[0].id;
      pbiIds.add(pbiId);

      const pbiColumnTimes: { [column: string]: number } = {};
      const significantRevisions = this.getSignificantRevisions(revisions);

      for (let i = 0; i < significantRevisions.length - 1; i++) {
        const currentRevision = significantRevisions[i];
        const nextRevision = significantRevisions[i + 1];
        const currentColumn = currentRevision.fields['System.BoardColumn'];
        const timeDiff = this.calculateTimeDiff(currentRevision, nextRevision);

        this.logPbiDetails(currentRevision, currentColumn, timeDiff, nextRevision, teamName);

        const isValidColumn = columnOrder.includes(currentColumn);
        if (currentColumn && isValidColumn) {
          pbiColumnTimes[currentColumn] = (pbiColumnTimes[currentColumn] || 0) + timeDiff;
        }
      }

      for (const column in pbiColumnTimes) {
        console.log(`[PBI Time By Column] - PBI #${pbiId} | Team: ${teamName} | Column: ${column} | Total Time: ${pbiColumnTimes[column].toFixed(2)} days`);
        aggregatedColumnTimes[column] = (aggregatedColumnTimes[column] || 0) + pbiColumnTimes[column];
      }
    });

    return { aggregatedColumnTimes, pbiIds };
  }

  /**
   * Calculates the average time spent in each column and the overall average time.
   * @param aggregatedColumnTimes An object mapping column names to their total time spent.
   * @param totalPbis The total number of unique PBIs processed.
   * @returns An object containing the average times per column and the overall average time.
   */
  private calculateAverageTimes(aggregatedColumnTimes: { [key: string]: number }, totalPbis: number): { averageColumnTimes: { [key: string]: number }, averageTotalTime: number } {
    const averageColumnTimes: { [key: string]: number } = {};
    let averageTotalTime = 0;

    if (totalPbis > 0) {
      for (const column in aggregatedColumnTimes) {
        const averageTime = aggregatedColumnTimes[column] / totalPbis;
        averageColumnTimes[column] = averageTime;
        averageTotalTime += averageTime;
      }
    }

    return { averageColumnTimes, averageTotalTime };
  }

  /**
   * Calculates the time difference in days between two revisions.
   * @param currentRevision The earlier revision.
   * @param nextRevision The subsequent revision.
   * @returns The time difference in fractional days.
   */
  private calculateTimeDiff(currentRevision: any, nextRevision: any): number {
    const startTime = new Date(currentRevision.fields['System.ChangedDate']).getTime();
    const endTime = new Date(nextRevision.fields['System.ChangedDate']).getTime();
    return (endTime - startTime) / (1000 * 60 * 60 * 24); // in days
  }

  /**
   * Filters a work item's revision history to include only the revisions where the board column changed.
   * This significantly speeds up the calculation by reducing the number of iterations.
   * @param revisions The full revision history for a single work item.
   * @returns A filtered array of revisions representing only the moments the column changed.
   */
  private getSignificantRevisions(revisions: any[]): any[] {
    if (!revisions || revisions.length === 0) {
      return [];
    }

    const significantRevisions = [revisions[0]];
    let lastColumn = revisions[0].fields['System.BoardColumn'];

    for (let i = 1; i < revisions.length; i++) {
      const currentColumn = revisions[i].fields['System.BoardColumn'];
      if (currentColumn !== lastColumn) {
        significantRevisions.push(revisions[i]);
        lastColumn = currentColumn;
      }
    }
    return significantRevisions;
  }

  /**
   * Logs the details of a PBI's time in a specific column for debugging purposes.
   * @param revision The current revision of the work item.
   * @param column The board column the work item was in.
   * @param timeDiff The time spent in that column, in days.
   * @param nextRevision The next revision, used to determine the end time.
   * @param teamName The name of the team for which the calculation is being run.
   */
  private logPbiDetails(revision: any, column: string, timeDiff: number, nextRevision: any, teamName: string): void {
    const nextColumn = nextRevision.fields['System.BoardColumn'];
    console.log(`[PBI Time By Column] - PBI #${revision.id} moved from '${column}' -> '${nextColumn}' after ${timeDiff.toFixed(2)} days`);
  }

  /**
   * Constructs the final `PbiTimeByColumnResult` object from the aggregated data.
   * It calculates percentages and handles the case where no data is found.
   * @param aggregatedColumnTimes An object mapping column names to the total time spent in them.
   * @param grandTotalTime The total time spent across all columns.
   * @param totalPbis The total number of unique PBIs processed.
   * @param allColumnsWithData A list of all columns that had data, for UI purposes.
   * @returns The final `PbiTimeByColumnResult` object.
   */
  private buildResult(
    aggregatedColumnTimes: { [column: string]: number }, 
    grandTotalTime: number, 
    totalPbis: number,
    allColumnsWithData: string[]
  ): PbiTimeByColumnResult {
    if (grandTotalTime === 0) {
      return {
        value: 0,
        displayValue: 'N/A',
        subtitle: 'No relevant PBI history found for team',
        columns: [],
        totalPbis: 0,
        allColumnsWithData: allColumnsWithData
      };
    }

    const columns = Object.keys(aggregatedColumnTimes).map(column => ({
      column,
      time: Math.round(aggregatedColumnTimes[column] * 10) / 10,
      percentage: grandTotalTime > 0 ? (aggregatedColumnTimes[column] / grandTotalTime) * 100 : 0
    }));

    let roundedPercentageSum = 0;
    columns.sort((a, b) => b.percentage - a.percentage);

    for (let i = 1; i < columns.length; i++) {
      const rounded = Math.round(columns[i].percentage);
      roundedPercentageSum += rounded;
      columns[i].percentage = rounded;
    }

    if (columns.length > 0) {
      columns[0].percentage = 100 - roundedPercentageSum;
    }

    return {
      value: grandTotalTime,
      displayValue: '',
      subtitle: '',
      columns: columns,
      totalPbis,
      allColumnsWithData
    };
  }

  /**
   * Sorts the calculated columns based on the official board column order.
   * @param columns The array of calculated column data.
   * @param columnOrder The official order of columns.
   * @returns The sorted array of columns.
   */
  private sortColumns(columns: { column: string; time: number; percentage: number }[], columnOrder: string[]): { column: string; time: number; percentage: number }[] {
    return columns.sort((a, b) => {
      const indexA = columnOrder.indexOf(a.column);
      const indexB = columnOrder.indexOf(b.column);
      return indexA - indexB;
    });
  }

  /**
   * Fetches the board columns for a specific team and project from ADO.
   * Uses a cache to prevent redundant API calls.
   * @param org The ADO organization name.
   * @param projectName The ADO project name.
   * @param teamName The name of the team.
   * @returns An Observable that emits an array of board column objects.
   */
  public getBoardColumns(org: string, projectName: string, teamName: string): Observable<any[]> {
    const cacheKey = `${projectName}-${teamName}`;
    if (this.boardColumnsCache.has(cacheKey)) {
      return of(this.boardColumnsCache.get(cacheKey)!);
    }

    return this.adoService.GetBoardColumns(org, projectName, teamName).pipe(
      tap(columns => {
        this.boardColumnsCache.set(cacheKey, columns);
      })
    );
  }
}
